import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ShoppingCart from '../../components/ui/ShoppingCart';
import ContactHub from '../../components/ui/ContactHub';
import HeroSection from './components/HeroSection';
import ServicesGrid from './components/ServicesGrid';
import TestimonialsCarousel from './components/TestimonialsCarousel';
import ProjectsPreview from './components/ProjectsPreview';
import ServiceAreasMap from './components/ServiceAreasMap';
import EmergencyBookingWidget from './components/EmergencyBookingWidget';

const Homepage = () => {
  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Clean360 - Professional Cleaning & Flooring Services | Trusted Local Experts</title>
        <meta 
          name="description" 
          content="Transform your space with Clean360's expert cleaning and flooring solutions. Residential & commercial services, emergency response, and premium installations. Book your free consultation today!" 
        />
        <meta name="keywords" content="cleaning services, flooring installation, carpet cleaning, deep cleaning, commercial cleaning, emergency cleanup, vinyl flooring, hardwood floors" />
        <meta property="og:title" content="Clean360 - Professional Cleaning & Flooring Services" />
        <meta property="og:description" content="Expert cleaning and flooring solutions with 24/7 emergency service. Trusted by 500+ customers with 4.9/5 rating." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://clean360.com" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <Header />

        {/* Main Content */}
        <main className="pt-16">
          {/* Hero Section */}
          <HeroSection />

          {/* Services Grid */}
          <ServicesGrid />

          {/* Testimonials Carousel */}
          <TestimonialsCarousel />

          {/* Projects Preview */}
          <ProjectsPreview />

          {/* Service Areas Map */}
          <ServiceAreasMap />

          {/* Emergency Booking Widget */}
          <EmergencyBookingWidget />
        </main>

        {/* Footer */}
        <footer className="bg-text-primary text-white py-16">
          <div className="container mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Company Info */}
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">C360</span>
                  </div>
                  <span className="text-xl font-semibold">Clean360</span>
                </div>
                <p className="text-white/80 mb-4 leading-relaxed">
                  Professional cleaning and flooring services you can trust. 
                  Transforming spaces with excellence since 2015.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-white/60 hover:text-white transition-colors">
                    <span className="sr-only">Facebook</span>
                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-sm">f</span>
                    </div>
                  </a>
                  <a href="#" className="text-white/60 hover:text-white transition-colors">
                    <span className="sr-only">Instagram</span>
                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-sm">ig</span>
                    </div>
                  </a>
                  <a href="#" className="text-white/60 hover:text-white transition-colors">
                    <span className="sr-only">Twitter</span>
                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-sm">tw</span>
                    </div>
                  </a>
                </div>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-lg font-semibold mb-6">Our Services</h4>
                <ul className="space-y-3">
                  <li><a href="/service-details" className="text-white/80 hover:text-white transition-colors">House Cleaning</a></li>
                  <li><a href="/service-details" className="text-white/80 hover:text-white transition-colors">Office Cleaning</a></li>
                  <li><a href="/service-details" className="text-white/80 hover:text-white transition-colors">Carpet Cleaning</a></li>
                  <li><a href="/service-details" className="text-white/80 hover:text-white transition-colors">Deep Cleaning</a></li>
                  <li><a href="/service-details" className="text-white/80 hover:text-white transition-colors">Flooring Installation</a></li>
                  <li><a href="/service-details" className="text-white/80 hover:text-white transition-colors">Emergency Services</a></li>
                </ul>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
                <ul className="space-y-3">
                  <li><a href="/projects-gallery" className="text-white/80 hover:text-white transition-colors">Project Gallery</a></li>
                  <li><a href="/resources-hub" className="text-white/80 hover:text-white transition-colors">Resources Hub</a></li>
                  <li><a href="/e-commerce-store" className="text-white/80 hover:text-white transition-colors">Shop Products</a></li>
                  <li><a href="/customer-portal" className="text-white/80 hover:text-white transition-colors">Customer Portal</a></li>
                  <li><a href="/smart-contact" className="text-white/80 hover:text-white transition-colors">Contact Us</a></li>
                  <li><a href="/admin-dashboard" className="text-white/80 hover:text-white transition-colors">Admin Dashboard</a></li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="text-lg font-semibold mb-6">Contact Info</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 mt-0.5 text-primary">📍</div>
                    <div className="text-white/80">
                      123 Business Ave<br />
                      Los Angeles, CA 90210
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 text-primary">📞</div>
                    <a href="tel:+15551234567" className="text-white/80 hover:text-white transition-colors">
                      (555) 123-4567
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 text-primary">✉️</div>
                    <a href="mailto:info@clean360.com" className="text-white/80 hover:text-white transition-colors">
                      info@clean360.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 text-primary">🕒</div>
                    <div className="text-white/80">
                      24/7 Emergency Service<br />
                      Mon-Fri: 8AM-6PM
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-white/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
              <div className="text-white/60 text-sm mb-4 md:mb-0">
                © {new Date()?.getFullYear()} Clean360. All rights reserved.
              </div>
              <div className="flex space-x-6 text-sm">
                <a href="#" className="text-white/60 hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-white/60 hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="text-white/60 hover:text-white transition-colors">Cookie Policy</a>
              </div>
            </div>
          </div>
        </footer>

        {/* Floating Components */}
        <ShoppingCart />
        <ContactHub />
      </div>
    </>
  );
};

export default Homepage;